import React, {useState} from "react"
import {CheckPayment} from "../../resources/checkPayments"
import {createCheckStopPayment, disableStopPayment, CheckStopPayment} from "../../resources/stopPayments"
import {useAccessToken} from "../../services/auth"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {Modal} from "../Modals/Modal"

interface StopCheckPaymentModalProps {
	checkPayment: CheckPayment
	closeModal: () => void
	onSuccess: () => void
	mode?: "create" | "remove"
	existingStopPayment?: CheckStopPayment
}

export function StopCheckPaymentModal({
	checkPayment,
	closeModal,
	onSuccess,
	mode = "create",
	existingStopPayment,
}: StopCheckPaymentModalProps) {
	const accessToken = useAccessToken()
	const [error, setError] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const [stopState, stopAction] = useAsyncResultIdle(createCheckStopPayment)

	const handleAction = async () => {
		setError("")
		setIsLoading(true)

		try {
			if (mode === "remove" && existingStopPayment) {
				const result = await disableStopPayment({
					id: existingStopPayment.id,
					accessToken,
				})
				result.match(
					() => {
						onSuccess()
						closeModal()
					},
					(err) => {
						const msg = err.errors?.[0]?.detail || "Failed to remove stop payment"
						setError(msg)
					}
				)
			} else {
				const result = await stopAction({
					accountId: checkPayment.relationships.account.data.id,
					checkNumber: checkPayment.attributes.checkNumber,
					amount: checkPayment.attributes.amount,
					targetCheckPaymentId: checkPayment.id,
					accessToken,
				})

				result.match(
					() => {
						onSuccess()
						closeModal()
					},
					(err) => {
						const msg = err.errors?.[0]?.detail || "Failed to stop check payment"
						setError(msg)
					}
				)
			}
		} catch (error) {
			setError("An unexpected error occurred")
		} finally {
			setIsLoading(false)
		}
	}

	const isAsyncLoading = stopState.isPending()

	const isLoadingState = isLoading || isAsyncLoading

	return (
		<Modal
			close={() => {
				closeModal()
			}}
			classname="check-payment-modal"
		>
			<div className="modal-content">
				<h3 className="title is-4">
					{mode === "remove"
						? "You are about to remove a check stop payment"
						: "You are about to stop this check payment"}
				</h3>
				<p className="mb-4">
					{mode === "remove"
						? "Removing a stop payment will allow this check to be deposited. This action is final and may have financial or legal consequences."
						: "Stopping a check prevents it from being cashed or deposited. This action is final and may have legal or financial implications."}
				</p>

				<p className="mb-4 has-text-weight-semibold">Are you sure you want to proceed?</p>

				<table className="table is-fullwidth check-payment-table">
					<thead>
						<tr>
							<th>ID</th>
							<th>Check Number</th>
							<th>Purpose</th>
							<th>Amount</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>{checkPayment.id}</td>
							<td>{checkPayment.attributes.checkNumber || "N/A"}</td>
							<td>{checkPayment.attributes.isAccountClosureCheck ? "Account Closure" : "General Use"}</td>
							<td>${(checkPayment.attributes.amount / 100).toFixed(2)}</td>
						</tr>
					</tbody>
				</table>

				{error && <div className="notification is-danger">{error}</div>}

				<div className="modal-actions">
					<div className="field is-grouped is-grouped-right">
						<div className="control">
							<button className="button do-not-button" onClick={closeModal} disabled={isLoadingState}>
								Cancel
							</button>
						</div>
						<div className="control">
							<button
								className={`button is-black ${isLoadingState ? "is-loading" : ""}`}
								onClick={handleAction}
								disabled={isLoadingState}
							>
								{mode === "remove" ? "Remove Stop Payment" : "Stop Check"}
							</button>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	)
}
